import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Instagram, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export default function CheckoutReturnPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [ready, setReady] = useState(false);
  const [instagramEnabled, setInstagramEnabled] = useState(false);
  const [account, setAccount] = useState<{ id: string; clubName: string } | null>(null);
  const [connecting, setConnecting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const t = setTimeout(() => setReady(true), 800);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.getUser();
      const user = data.user;
      if (!user) return;
      const { data: club } = await supabase
        .from("clubs")
        .select("club_name, instagram_enabled, instagram_connected")
        .eq("user_id", user.id)
        .maybeSingle();
      setAccount({ id: user.id, clubName: club?.club_name ?? "" });
      const fromStorage = localStorage.getItem("torflash_instagram") === "1";
      setInstagramEnabled(
        Boolean((club?.instagram_enabled ?? fromStorage) && !club?.instagram_connected),
      );
    })();
  }, []);

  const connectInstagram = async () => {
    if (!account) return;
    setConnecting(true);
    const { data, error } = await supabase.functions.invoke("instagram-connect", {
      body: { accountId: account.id, clubName: account.clubName },
    });
    setConnecting(false);
    if (error || !data?.url) {
      toast({
        title: "Instagram-Verbindung nicht möglich",
        description: "Bitte versuch es gleich nochmal oder schreib uns an info@torflash.de.",
        variant: "destructive",
      });
      return;
    }
    window.open(data.url, "_blank", "noopener");
  };

  return (
    <div className="container flex min-h-[60vh] max-w-lg flex-col items-center justify-center text-center">
      {!ready ? (
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      ) : sessionId ? (
        <>
          <CheckCircle2 className="h-12 w-12 text-primary" />
          <h1 className="mt-4 font-display text-2xl font-bold tracking-tight">
            Zahlung abgeschlossen
          </h1>
          {instagramEnabled ? (
            <>
              <p className="mt-2 text-muted-foreground">
                Danach laufen eure Grafiken automatisch auf eurem Instagram.
              </p>
              <Button
                variant="hero"
                size="lg"
                className="mt-6 w-full gap-2"
                disabled={connecting || !account}
                onClick={connectInstagram}
              >
                {connecting ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Instagram className="h-5 w-5" />
                )}
                Jetzt Instagram verbinden
              </Button>
              <Link to="/mein-verein" className="mt-3">
                <Button variant="ghost">Später im Dashboard</Button>
              </Link>
            </>
          ) : (
            <>
              <p className="mt-2 text-muted-foreground">
                Danke! Dein Verein ist aktiviert. Die Anleitung für Telegram bekommst du per E-Mail.
              </p>
              <Link to="/mein-verein" className="mt-6">
                <Button variant="hero">Zum Dashboard</Button>
              </Link>
            </>
          )}
        </>
      ) : (
        <>
          <h1 className="font-display text-2xl font-bold tracking-tight">Keine Zahlung gefunden</h1>
          <Link to="/mein-verein" className="mt-6">
            <Button variant="outline">Zum Dashboard</Button>
          </Link>
        </>
      )}
    </div>
  );
}
